"use client";

import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";

import { CreateTeamDialog } from "@/components/dialog/create-team/CreateTeamDialog";
import { TeamDialog } from "@/components/dialog/team/TeamDialog";
import { UpgradePlanDialog } from "@/components/dialog/upgrade-plan/UpgradePlanDialog";
import { BottomMenu } from "@/components/doc/left-sidebar/bottom-menu/BottomMenu";
import { TopMenu } from "@/components/doc/left-sidebar/top-menu/TopMenu";
import { SearchList } from "@/components/search/SearchList";
import { Sidebar } from "@/components/sidebar";
import { SidebarVisibilityToggle } from "@/components/sidebar/SidebarVisibilityToggle";
import { useStore } from "@/store";
import { useDirectChats } from "@/store/useDirectChats";
import { useRecentDocuments } from "@/store/useRecentDocuments";
import { useTeamChats } from "@/store/useTeamChats";
import { twMerge } from "@/utils/twMerge";
import { useShowPublicProfile } from "@/utils/useShowPublicProfile";

import { ChatsTab } from "./chats-tab/ChatsTab";
import { MiddleMenu } from "./middle-menu/MiddleMenu";
import { PersonalDocumentTree } from "./personal-document-tree/PersonalDocumentTree";
import { PublicProfile } from "./public-profile/PublicProfile";

const Content = observer(() => {
  const { auth, search, view } = useStore();
  const showPublicProfile = useShowPublicProfile();

  if (view.leftSidebarState === "hidden") {
    return null;
  }

  const renderContent = () => {
    if (view.isLeftSidebarCollapsed) {
      return <MiddleMenu />;
    }

    if (!auth.isLoggedIn || showPublicProfile) {
      return search.isOpen && search.searchQuery ? <SearchList /> : <PublicProfile />;
    }

    return view.leftSidebarTab === "chats" ? <ChatsTab /> : <PersonalDocumentTree />;
  };

  return (
    <div
      className={twMerge(
        "flex flex-col flex-1 h-full",
        view.isLeftSidebarCollapsed && "relative overflow-y-auto no-scrollbar -m-2.5 p-2.5",
      )}
    >
      <TopMenu />
      {renderContent()}
      {/* We decided to hide the banner for now. We don't really update it and it looks like spam a bit. */}
      {/* <SidebarInfoBanner /> */}
      {/* <SidebarFooter className="mt-auto" /> */}
      <BottomMenu />
    </div>
  );
});

export const LeftSidebar = observer(function LeftSidebar() {
  const { docId: currentDocId, chatId, folderId } = useParams<{ [key: string]: string }>();
  const { auth, documentTree, view } = useStore();
  const { leftSidebarState, isLeftSidebarCollapsed, isSidebarLayoutReady } = view;
  const teamChats = useTeamChats();
  const directChats = useDirectChats();
  const recentDocuments = useRecentDocuments();
  const showPublicProfile = useShowPublicProfile();
  const t = useTranslations("documentsSidebar");
  // isLoggedIn is true from the SSR cookie before Firebase restores the user,
  // so the visit request has to wait for the user to load to carry a token.
  const isUserLoaded = !!auth.currentUser;

  useEffect(() => {
    if (currentDocId && auth.isLoggedIn && !showPublicProfile) {
      documentTree.setCurrentDoc(currentDocId);
      if (isUserLoaded) recentDocuments.recordVisit(currentDocId);
    } else if (chatId) {
      documentTree.clearCurrentDoc();
    }
  }, [
    documentTree,
    recentDocuments,
    currentDocId,
    chatId,
    auth.isLoggedIn,
    isUserLoaded,
    showPublicProfile,
  ]);

  useEffect(() => {
    if (!chatId && (currentDocId || folderId)) view.setLeftSidebarTab("docs");
  }, [chatId, currentDocId, folderId, view]);

  const isKnownChat = !!chatId && !!(teamChats.getChat(chatId) || directChats.getChat(chatId));
  const areChatsLoaded =
    (teamChats.status === "loaded" || !teamChats.isAvailable) &&
    (directChats.status === "loaded" || directChats.status === "error");

  useEffect(() => {
    if (!chatId) return;
    if (isKnownChat) {
      view.setLeftSidebarTab("chats");
    } else if (areChatsLoaded) {
      view.setLeftSidebarTab("docs");
    }
  }, [chatId, view, isKnownChat, areChatsLoaded]);

  return (
    <>
      <Sidebar.Root
        aria-label={t("label")}
        isHidden={leftSidebarState === "hidden"}
        className={twMerge(
          "group/left-sidebar print:hidden sidebar-focus-ring",
          isLeftSidebarCollapsed && "w-[54px] min-w-[54px] pr-0",
          !isSidebarLayoutReady && "invisible",
        )}
        side="left"
      >
        {leftSidebarState !== "hidden" && (
          <SidebarVisibilityToggle
            position="left"
            className={twMerge(
              "absolute z-50 top-5 -mr-[30px]",
              leftSidebarState === "full" ? "right-[9px]" : "right-[-1px]",
            )}
          />
        )}
        <Content />
      </Sidebar.Root>
      <TeamDialog />
      <UpgradePlanDialog />
      <CreateTeamDialog />
    </>
  );
});
